import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { CHANNEL, searchPageSlugs } from '@boss/constants/b2b-b2c';
import { useRouter } from '@boss/hooks';
import { LOCALE_CODE } from '@boss/types/b2b-b2c';
import { appendValueToSlugsObject, getQueryKeyValue } from '@boss/utils';

import { SEO } from '../components';
import { PageTypes } from '../constants';
import { SearchPage } from '../containers';
import Layout from '../layout/default';
import getServerSideTranslations from '../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket } from '../utils/localeMapper';
import { getMappedNavigation } from '../utils/navigation';

export const getStaticProps = async ({ locale, preview }: GetStaticPropsContext) => {
  if (!locale || locale === 'default' || !CHANNEL) {
    return {
      notFound: true,
    };
  }

  const contenfulLocale = mapNextToContentfulLocaleMarket(locale);

  const navigation = await getMappedNavigation(contenfulLocale, !!preview);

  return {
    props: {
      pageType: PageTypes.SEARCH,
      navigation,
      preview: !!preview,
      locale: locale as LOCALE_CODE,
      ...(await getServerSideTranslations(locale, ['common', 'search', 'facets', 'forms', 'color'])),
    },
  };
};

const Search = ({ preview, navigation, locale }: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { query } = useRouter();
  const { t } = useTranslation('common');
  const crumbles = [{ href: searchPageSlugs[locale], label: t('search') }];
  const queryValue = getQueryKeyValue(query, 'q');
  const pageTitle = t('title', { ns: 'search', query: queryValue }) ?? '';
  const pageDescription = pageTitle; // TODO: Update with final value once this is known

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      localizedSlugs={searchPageSlugs}
      navigation={navigation}
      noFollow
      noIndex
      title={pageTitle}
    >
      <Layout
        childrenClassName="min-h-[calc(100vh-15rem)]" // Min-height to avoid collapsed grid on initial render due to InstantSearch client side search
        crumbles={crumbles}
        localizedSlugs={appendValueToSlugsObject(`?${queryValue}`, searchPageSlugs)}
        navigation={navigation}
        preview={preview}
      >
        <SearchPage />
      </Layout>
    </SEO>
  );
};

export default Search;
